import { JSX, ReactElement, useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { IChecklistItem, IDocument } from 'src/shared/types/Documents';
import { GridItemMenu } from './GridMenuItem';
import { CheckboxSelections, Tooltip } from '@ds';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import styles from './DocumentsGrid.module.scss';
import { DocumentResponse } from 'src/shared/types/Document';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import classNames from 'classnames';
import { ACCEPTED_FILE_TYPES } from 'src/utils/FileUtils';

export interface DocumentGridProps {
  documents: IDocument[] | File[] | DocumentResponse[];
  currentItem?: IChecklistItem;
  isReadonly?: boolean;
  emptyMessage?: JSX.Element;
  isPassport?: boolean;
  isCaseActive?: boolean;
  onRefetchDocuments?: () => void;
  onRefetchAdditionalDocuments?: () => void;
  onDeleteFile?: (fileIndex: number) => void;
  isSelectable?: boolean;
  selectedDocuments?: DocumentResponse[];
  onDocumentToggle?: (doc: DocumentResponse) => void;
  showMenu?: boolean;
}

const DocumentsGrid = (props: DocumentGridProps) => {
  const {
    currentItem,
    onRefetchDocuments,
    isReadonly,
    onRefetchAdditionalDocuments,
    isPassport = false,
    onDeleteFile,
    isSelectable = false,
    selectedDocuments,
    onDocumentToggle,
    showMenu = true,
  } = props;
  const [currItem, setCurrItem] = useState(false);

  const getFileExtensionKey = (doc: IDocument | File | DocumentResponse): string => {
    let fileName = '';

    if ('documentFiles' in doc && doc.documentFiles?.[0]?.fileName) {
      fileName = doc.documentFiles[0].fileName;
    } else if ('name' in doc) {
      fileName = doc.name;
    } else if ('files' in doc && Array.isArray(doc.files) && doc.files.length > 0) {
      fileName = doc.files[0];
    }

    const extension = fileName.split('.').pop()?.toLowerCase() || 'unknown';
    return ACCEPTED_FILE_TYPES.includes(extension) ? extension : 'unknown';
  };

  const renderCardHeader = (extensionKey: string, document: IDocument | File | DocumentResponse) => {
    const documentId = 'docId' in document && document.docId ? parseInt(document.docId.toString()) : 0;
    const isCarriedForwardDocument = currentItem?.checklistItemDocuments.some(
      (d) => d.isAutoCarryForward && d.documentId === documentId
    );

    return (
      <div className="document-thumbnail">
        {isCarriedForwardDocument && (
          <>
            <Tooltip
              target={'id' in document ? `.carried-forward-wrapper-${document.id}` : undefined}
              content="Imported document from a previous case"
              position="bottom"
              style={{ minWidth: '300px' }}
            />
            {'id' in document && (
              <div className={`new-carried-forward-wrapper carried-forward-wrapper-${document.id}`}></div>
            )}
          </>
        )}
        <div
          draggable={false}
          // Combine a base class plus an extension-specific class
          className={`${styles['fileThumb']} ${styles[extensionKey + '-icon']}`}
        />
      </div>
    );
  };

  useEffect(() => {
    if (currentItem) {
      setCurrItem(currentItem.checklistItemDocuments?.length > 0);
    } else {
      setCurrItem(true);
    }
  }, [currentItem, props.documents]);

  const renderGridItem = (doc: IDocument | File | DocumentResponse, index: number): ReactElement | null => {
    if (!doc) return null;

    const extensionKey = getFileExtensionKey(doc);
    const cardHeader = renderCardHeader(extensionKey, doc);

    let fileName = '-';
    if ('documentName' in doc && doc.documentName) {
      fileName = doc.documentName;
    } else if ('docName' in doc && doc.docName) {
      fileName = doc.docName;
    } else if ('name' in doc) {
      fileName = doc.name;
    }

    const isChecked = selectedDocuments
      ? selectedDocuments?.some((selectedDoc) => {
          if ('docId' in doc && 'docId' in selectedDoc) {
            return doc.docId === selectedDoc.docId;
          }
          if ('id' in doc && 'id' in selectedDoc) {
            return doc.id === selectedDoc.id;
          }
          return false;
        })
      : false;

    const getUploadedDateFromEpoch = (epoch: number | string) => {
      let epochNum = typeof epoch === 'string' ? Number(epoch) : epoch;
      if (epochNum < 1e12) {
        epochNum = epochNum * 1000;
      }
      const date = new Date(epochNum);
      return getFormattedDate(date.toLocaleString(), UserDateFormat.get());
    };

    const cardContent = (
      <div className={styles['card-content']}>
        <div className="overflow-hidden">
          <div className={styles['file-name-wrapper']}>
            <p>{fileName}</p>
          </div>
          {'createdEpochTime' in doc && doc.createdEpochTime && (
            <p className={styles['uploaded-date']}>
              UPLOADED: {typeof doc.createdEpochTime === 'number' ? getUploadedDateFromEpoch(doc.createdEpochTime) : ''}
            </p>
          )}
          {'docId' in doc && doc.createdDate && (
            <p className={styles['uploaded-date']}>
              UPLOADED: {getFormattedDate(new Date(doc.createdDate).toISOString(), UserDateFormat.get())}
            </p>
          )}
        </div>
        {showMenu &&
          (!isSelectable ? (
            'docId' in doc ? (
              <GridItemMenu
                document={doc}
                fileIndex={index}
                currentItem={currentItem ?? ({} as IChecklistItem)}
                onRefetchDocuments={onRefetchDocuments}
                onRefetchAdditionalDocuments={onRefetchAdditionalDocuments}
                isReadonly={isReadonly}
                isPassport={isPassport}
              />
            ) : (
              <GridItemMenu document={doc} fileIndex={index} isFile={true} onDeleteFile={onDeleteFile} />
            )
          ) : (
            <CheckboxSelections
              className={`m-2 ${styles['active-only-checkbox']}`}
              options={[{ label: '', checked: isChecked }]}
              onCheckboxChanged={() => {
                onDocumentToggle && onDocumentToggle(doc as DocumentResponse);
              }}
            />
          ))}
      </div>
    );

    return (
      <div className={styles['card-container']}>
        {currItem && (
          <Card header={cardHeader} className={styles['card-style']}>
            {cardContent}
          </Card>
        )}
      </div>
    );
  };

  const isMobile = useScreenSize().isMobile;
  const isTablet = useScreenSize().isTablet;

  const getGridCardClassName = () => {
    if (isSelectable && isMobile) {
      return classNames(styles['documents-grid-card'], styles['documents-grid-card-small']);
    } else if (isSelectable && isTablet) {
      return classNames(styles['documents-grid-card'], styles['documents-grid-card-tablet']);
    } else if (isSelectable) {
      return classNames(styles['documents-grid-card'], styles['documents-grid-card-selectable']);
    } else {
      return classNames(styles['documents-grid-card'], styles['documents-grid-card-standard']);
    }
  };

  return (
    <div className={styles['documents-grid']}>
      {props.documents.length > 0 ? (
        <div className="p-grid dataview">
          {props.documents.map((doc, index) => (
            <div key={index} className={getGridCardClassName()}>
              {renderGridItem(doc, index)}
            </div>
          ))}
        </div>
      ) : (
        props.emptyMessage || null
      )}
    </div>
  );
};

export default DocumentsGrid;
